import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import DiscountBundleWarningModal from 'components/DiscountBundleWarningModal';
import Link from 'components/Link';
import isBrowser from 'utils/is-browser';
import styles from './DiscountBundleBuilder.module.scss';

const DEFAULT_STEP_TEXT = 'Checking your bag for bundle items';
const DEFAULT_INSTRUCTION_TEXT = 'Please select items at each step of the way';

function getStepInfo({
  productsInBag,
  discountThreshold,
  allFulfilled,
  stepIndex,
  isMultiContraint,
}) {
  if (productsInBag === null || discountThreshold === null) {
    return DEFAULT_STEP_TEXT;
  }

  const remaining = discountThreshold - productsInBag;
  const itemText = remaining > 1 ? 'items' : 'item';
  const stepNum = stepIndex + 1;

  return productsInBag >= discountThreshold ? (
    allFulfilled ? (
      <span>
        Bundle Complete{' - '}
        <Link
          className="font-roboto fs-8 fw-400 text-decoration-underline"
          href="/specials"
        >
          Back to Specials
        </Link>
      </span>
    ) : (
      <span>Step Complete</span>
    )
  ) : isMultiContraint ? (
    `Step ${stepNum} - Add ${remaining} ${itemText} to complete`
  ) : (
    `Add ${remaining} ${itemText} to complete`
  );
}

export default function DiscountCounter({ discount, products, setProducts }) {
  const { lineItems } = useSelector(({ cart }) => cart);
  const [stepResults, setStepResults] = useState(null);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [stepProductsMap, setStepProductsMap] = useState(0);
  const steps = discount?.isBundledDiscount
    ? discount?.constraints
    : [...discount.constraints, discount.reward];
  const numberOfSteps = steps.length;

  // build map so we can go from currentStepIndex to
  useEffect(() => {
    const newStepProductMap = steps.map((s) => {
      const stepSkuSet = new Set(s.products.map((p) => p.sku));
      const stepProducts = products.filter((p) => stepSkuSet.has(p.SKU));
      return stepProducts;
    });
    setStepProductsMap(newStepProductMap);
  }, [products]);

  // Pass filtered products outside component
  useEffect(() => {
    if (stepProductsMap) {
      setProducts(stepProductsMap[currentStepIndex]);
    }
  }, [currentStepIndex, stepProductsMap]);

  useEffect(() => {
    if (!discount || !lineItems) return;
    const skuQuantityMap = Object.fromEntries(
      lineItems.map((li) => [li.SKU, li.quantity])
    );

    let newStepResults = [];
    let nextUnfulfilledIndex;
    let allFulfilled = true;

    for (let i = 0; i < numberOfSteps; i++) {
      const { discountThreshold, products: restrictionProducts } = steps[i];
      let productsInBag = 0;
      let fulfilled = false;

      for (const { sku } of restrictionProducts) {
        productsInBag += skuQuantityMap[sku] ?? 0;
        if (productsInBag >= discountThreshold) {
          fulfilled = true;
        }
      }
      if (!fulfilled) {
        allFulfilled = false;
        if (
          nextUnfulfilledIndex === undefined ||
          nextUnfulfilledIndex <= currentStepIndex
        ) {
          nextUnfulfilledIndex = i;
        }
      }

      newStepResults.push({
        fulfilled,
        discountThreshold,
        productsInBag,
      });
    }

    newStepResults = newStepResults.map((ret, i) => {
      return {
        ...ret,
        stepInfo: getStepInfo({
          productsInBag: ret.productsInBag,
          discountThreshold: ret.discountThreshold,
          allFulfilled,
          stepIndex: i,
          isMultiContraint: discount.isMultiStep,
        }),
      };
    });

    const isExact =
      newStepResults[currentStepIndex].productsInBag ===
        newStepResults[currentStepIndex].discountThreshold &&
      newStepResults[currentStepIndex].discountThreshold > 0;

    if (isBrowser && isExact) {
      const nextStep = nextUnfulfilledIndex ?? numberOfSteps - 1;
      if (nextStep !== currentStepIndex) {
        setTimeout(() => {
          window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth',
          });
          setCurrentStepIndex(nextStep);
        }, 400);
      }
    }
    setStepResults(newStepResults);
  }, [lineItems]);

  return discount.isMultiStep ||
    stepResults?.[currentStepIndex]?.discountThreshold ? (
    <>
      {numberOfSteps > 1 && (
        <>
          <div className="w-100 d-flex flex-row flex-wrap justify-content-between">
            <div className="mx-auto font-frl text-primary fs-16">
              {discount?.onlineNameOverride === ''
                ? discount?.onlineName
                : discount?.onlineNameOverride}
            </div>
          </div>
          <div className="w-100 d-flex flex-row flex-wrap justify-content-between">
            <div className="mx-auto text-primary pb-4">
              {steps?.[currentStepIndex]?.instructionOverride ||
                DEFAULT_INSTRUCTION_TEXT}
            </div>
          </div>
        </>
      )}
      <div
        className={classNames(
          `rounded w-100 mb-8 p-4`,
          styles.discount_counter,
          { [styles.discount_counter_single_constraint]: numberOfSteps === 1 }
        )}
      >
        <button
          className={classNames(
            'position-absolute border-0 bg-transparent font-sofia px-0',
            numberOfSteps === 1
              ? styles.close_button_single_constraint
              : styles.close_button
          )}
          id="bundle_close"
          title="Close Bundle"
          type="button"
          onClick={() => {
            setShowWarningModal(true);
          }}
        >
          <span>
            <FontAwesomeIcon className={styles.icon} icon={faXmark} />
          </span>
        </button>
        {discount.isMultiStep && (
          <div
            className={classNames(
              `d-flex flex-row flex-wrap justify-content-evenly`
            )}
          >
            <button
              type="button"
              className={classNames(
                'btn btn-transparent font-roboto lh-1',
                styles.arrow_button,
                styles.arrow_button_left
              )}
              disabled={currentStepIndex === 0}
              onClick={() => {
                setCurrentStepIndex(currentStepIndex - 1);
              }}
            >
              {`<`}
            </button>
            {stepResults?.map(({ fulfilled }, i) => {
              const keyVal = `constraint-${i}`;
              return (
                <button
                  key={keyVal}
                  type="button"
                  className={classNames(
                    `btn btn-transparent mb-4 p-0 pb-2 text-primary fs-14 lh-1 font-roboto`,
                    styles.constraint_number,
                    {
                      'bg-secondary': fulfilled,
                      'fw-bold': i === currentStepIndex,
                      [styles.current_constraint]: i === currentStepIndex,
                      [styles.current_unfulfilled_constraint]:
                        i === currentStepIndex && !fulfilled,
                    }
                  )}
                  onClick={() => {
                    setCurrentStepIndex(i);
                  }}
                >
                  {i + 1}
                </button>
              );
            })}{' '}
            <button
              type="button"
              className={classNames(
                'btn btn-transparent font-roboto lh-1 p-0',
                styles.arrow_button,
                styles.arrow_button_right
              )}
              disabled={currentStepIndex === numberOfSteps - 1}
              onClick={() => {
                setCurrentStepIndex(currentStepIndex + 1);
              }}
            >
              {`>`}
            </button>
          </div>
        )}
        <div
          className={classNames(
            styles.step_text,
            `font-sofia fw-700 fs-9 rounded-pill text-center text-primary border border-2 border-primary mb-5 px-5 px-md-10 py-1 py-md-3 ${
              stepResults?.[currentStepIndex].fulfilled
                ? 'bg-secondary'
                : 'bg-white'
            }`
          )}
        >
          {stepResults?.[currentStepIndex].stepInfo}
        </div>
      </div>
      <DiscountBundleWarningModal
        showModal={showWarningModal}
        onCancel={() => {
          setShowWarningModal(false);
        }}
      />
    </>
  ) : null;
}
